import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

import "../css/homepage-redesign.css";

const highlightCards = [
  {
    eyebrow: "For customers",
    title: "A digital wallet for local rewards",
    text: "Join different merchant reward programs and keep local loyalty, offers, and discovery together inside the Cling app.",
  },
  {
    eyebrow: "For merchants",
    title: "Launch loyalty fast. Bring customers back.",
    text: "Cling helps local businesses start a rewards program easily, get discovered, promote offers, collect reviews, and build repeat customer behavior.",
  },
  {
    eyebrow: "Built for what comes next",
    title: "Loyalty is the hook. Storefront, offers, and AI expand the value.",
    text: "Merchants can start with rewards, then showcase products, create offers, and soon use AI assistance to help customers choose better.",
  },
];

const featureCards = [
  {
    title: "Easy loyalty setup",
    body: "Enable a merchant rewards program without heavy setup, separate hardware, or a complicated back office.",
  },
  {
    title: "Rewards wallet",
    body: "Let customers keep multiple local merchant reward relationships in one Cling app instead of scattered cards and one-off apps.",
  },
  {
    title: "Local discovery",
    body: "Help customers find nearby shops, restaurants, services, and people through a cleaner local discovery experience.",
  },
  {
    title: "Business profiles",
    body: "Give every merchant a digital storefront that brings together story, products, photos, reviews, and direct engagement.",
  },
  {
    title: "Offers that convert",
    body: "Promote offers and deals that help businesses stay visible and give customers a reason to act now.",
  },
  {
    title: "Loyalty journeys",
    body: "Reward repeat customers with a loyalty program that feels simple for both the merchant and the customer.",
  },
  {
    title: "QR touchpoints",
    body: "Use a single QR code to connect offline footfall to online discovery, reviews, offers, and rewards.",
  },
];

const screenshotCards = [
  {
    image: require("../assets/explainer/business-list.png"),
    alt: "Cling all businesses merchant portfolio screenshot",
    label: "Merchant portfolio",
  },
  {
    image: require("../assets/explainer/business-detail-products.png"),
    alt: "Cling merchant storefront screenshot",
    label: "Merchant storefront",
  },
  {
    image: require("../images/945x2016-2111.png"),
    alt: "Cling clubs for you screenshot",
    label: "Clubs for you",
  },
];

const tourPoints = [
  "Start with customer onboarding, then move into the private profile and merchant setup flow.",
  "See business creation, product setup, offer management, rewards configuration, and merchant dashboards.",
  "Follow the customer side into posts, feed activity, offer viewing, and the rewards wallet experience.",
];

const businessPillars = [
  "Enable a rewards program easily and give customers a simple reason to return.",
  "Showcase your products, promote offers, and present your business with more depth than a static listing.",
  "Collect reviews, build trust, and stay connected to your local community through one digital storefront.",
];

const loyaltyPoints = [
  "Merchants can start a loyalty program quickly without complex setup.",
  "Customers can join multiple merchant rewards programs in one Cling experience.",
  "QR, offers, and profiles make loyalty part of a broader local commerce journey.",
];

const aiPoints = [
  "Help shoppers understand products faster and make better purchase decisions.",
  "Support merchants with a smarter digital storefront experience over time.",
  "Extend Cling beyond rewards into guided local commerce without changing the core product story today.",
];

const contactOptions = [
  {
    label: "Email",
    value: "admin@clubinweb.in",
    href: "mailto:admin@clubinweb.in",
  },
  {
    label: "Phone",
    value: "+91 90146 69227",
    href: "tel:+919014669227",
  },
];

const noAppPoints = [
  "Customers can still leave ratings and reviews on the web even if they have not installed the app yet.",
  "That gives merchants a useful first interaction with walk-in traffic instead of a dead-end scan.",
  "The full standee experience for loyalty, offers, and deeper engagement opens up inside the Cling app.",
];

const trustLinks = [
  {
    title: "Privacy notice",
    copy: "Read how Cling collects, processes, and protects user information.",
    to: "/PrivacyPolicy",
    cta: "View privacy policy",
  },
  {
    title: "Product explainer",
    copy: "Watch a guided walkthrough built from real Cling app screens across signup, business setup, loyalty, offers, and QR discovery.",
    to: "/VideoPlayer",
    cta: "Watch the explainer",
  },
  {
    title: "Delete account",
    copy: "Users can directly authenticate and request deletion of their Cling account and related data.",
    to: "/UserAccountDelete",
    cta: "Open deletion form",
  },
  {
    title: "Community examples",
    copy: "Preview live-style content and business posts already supported by the platform.",
    to: "/PostsPage",
    cta: "Browse posts",
  },
];

const leadershipTeam = [
  {
    name: "Sesh Gonella",
    role: "Founder & CEO",
    image: require("../images/authors/sesh.jpg"),
    bio: "Sesh brings more than 25 years of leadership experience across product, technology, and digital platforms. He founded Cling to help local communities and businesses thrive through connected digital experiences.",
  },
  {
    name: "Dr. SR Tulasi",
    role: "Co-Founder",
    image: require("../images/authors/SRTulasi.jpg"),
    bio: "Dr. Tulasi is a seasoned executive with deep operating experience across HR, industrial relations, and engineering. He helps shape Cling’s long-term business direction and grassroots growth strategy.",
  },
  {
    name: "Mr. K. Venkatram",
    role: "Finance Director",
    image: require("../images/authors/KVenkram.jpg"),
    bio: "Venkatram brings significant experience in investment banking and corporate finance across India and the Middle East, helping guide Cling’s financial discipline and strategic scaling.",
  },
  {
    name: "Ramesh Singu",
    role: "Head of Product Experience",
    image: require("../images/authors/avtar.png"),
    bio: "Ramesh leads product experience with a user-first mindset, shaping how discovery, merchant engagement, and community interactions feel inside Cling.",
  },
  {
    name: "Narsing Rao",
    role: "Head of Engineering",
    image: require("../images/authors/avtar.png"),
    bio: "Narsing leads engineering execution across the platform, focusing on secure, scalable systems that can support Cling’s next stage of growth.",
  },
  {
    name: "Prema Saktivel",
    role: "Head of Field Marketing",
    image: require("../images/authors/Prema.jpg"),
    bio: "Prema drives field marketing and local activation, helping Cling build strong merchant relationships and on-the-ground momentum across cities.",
  },
];

const HomePage = () => {
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Cling | Discover Nearby Businesses, Offers, Loyalty, and Community</title>
        <meta
          name="description"
          content="Cling helps merchants launch easy loyalty programs, showcase products, run offers, and keep customers coming back while giving customers one place to manage local rewards."
        />
      </Helmet>

      <div className="cling-home">
        <header className="cling-home__header">
          <div className="cling-home__container cling-home__header-inner">
            <a className="cling-home__brand" href="/" aria-label="Cling home">
              <img
                className="cling-home__brand-logo"
                src={require("../images/logo.svg").default}
                alt="Cling"
              />
            </a>

            <nav className="cling-home__nav" aria-label="Primary">
              <button type="button" onClick={() => scrollToSection("product-story")}>
                Product
              </button>
              <button type="button" onClick={() => scrollToSection("loyalty-wallet")}>
                Loyalty
              </button>
              <button type="button" onClick={() => scrollToSection("product-tour")}>
                Tour
              </button>
              <button type="button" onClick={() => scrollToSection("app-preview")}>
                Screens
              </button>
              <button type="button" onClick={() => scrollToSection("businesses")}>
                For Businesses
              </button>
              <Link to="/PrivacyPolicy">Trust & Safety</Link>
              <Link to="/UserAccountDelete">Delete Account</Link>
            </nav>

            <div className="cling-home__header-actions">
              <a
                className="cling-home__text-link"
                href="https://play.google.com/store/apps/details?id=com.cling1.app"
                target="_blank"
                rel="noreferrer"
              >
                Get the app
              </a>
              <a
                className="cling-home__store-button"
                href="https://play.google.com/store/apps/details?id=com.cling1.app"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={require("../images/playstore.svg").default}
                  alt="Download Cling on Google Play"
                />
              </a>
            </div>
          </div>
        </header>

        <main>
          <section className="cling-home__hero">
            <div className="cling-home__hero-gradient" aria-hidden="true" />
            <div className="cling-home__container cling-home__hero-layout">
              <div className="cling-home__hero-copy">
                <p className="cling-home__eyebrow">Loyalty-led local growth for merchants and customers</p>
                <h1>
                  Run a better business. Keep every local reward in one place.
                </h1>
                <p className="cling-home__hero-text">
                  Cling helps merchants launch easy rewards programs, showcase products, create
                  offers, and keep customers coming back while giving customers a digital wallet
                  for local rewards, offers, reviews, and discovery.
                </p>

                <div className="cling-home__hero-actions">
                  <a
                    className="cling-home__button cling-home__button--primary"
                    href="https://play.google.com/store/apps/details?id=com.cling1.app"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download on Google Play
                  </a>
                  <Link className="cling-home__button cling-home__button--secondary" to="/VideoPlayer">
                    Watch the explainer
                  </Link>
                </div>

                <div className="cling-home__hero-metrics">
                  <div>
                    <strong>Start loyalty fast</strong>
                    <span>Launch a merchant rewards program without heavy setup or extra friction</span>
                  </div>
                  <div>
                    <strong>Carry rewards in one app</strong>
                    <span>Customers can join multiple local programs and manage them inside Cling</span>
                  </div>
                  <div>
                    <strong>Grow beyond rewards</strong>
                    <span>Products, offers, reviews, and AI tee up a broader local-commerce platform</span>
                  </div>
                </div>
              </div>

              <div className="cling-home__hero-visual">
                <div className="cling-home__device-cluster">
                  <div className="cling-home__device cling-home__device--back">
                    <img
                      src={require("../assets/explainer/business-list.png")}
                      alt="Cling all businesses merchant portfolio screen"
                    />
                  </div>
                  <div className="cling-home__device cling-home__device--front">
                    <img
                      src={require("../assets/explainer/business-detail-products.png")}
                      alt="Cling merchant storefront screen"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="cling-home__section" id="product-story">
            <div className="cling-home__container">
              <div className="cling-home__section-heading">
                <p className="cling-home__eyebrow">Why Cling feels different</p>
                <h2>For merchants, loyalty is the starting point. Cling grows into a smarter local storefront over time.</h2>
                <p>
                  Cling works best when merchants want an easy way to bring customers back and
                  customers want one place to keep local rewards, discover businesses, and act on offers.
                </p>
              </div>

              <div className="cling-home__highlights">
                {highlightCards.map((card) => (
                  <article className="cling-home__highlight-card" key={card.title}>
                    <p className="cling-home__mini-eyebrow">{card.eyebrow}</p>
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="cling-home__section cling-home__section--tour" id="product-tour">
            <div className="cling-home__container">
              <div className="cling-home__section-heading cling-home__section-heading--split">
                <div>
                  <p className="cling-home__eyebrow">Product tour</p>
                  <h2>Walk through the real Cling product flow without leaving the site.</h2>
                </div>
                <p>
                  The explainer now follows the actual sequence from onboarding into private
                  profile, business creation, products, offers, loyalty, customer content, and QR
                  discovery.
                </p>
              </div>

              <div className="cling-home__tour-panel">
                <div className="cling-home__tour-copy">
                  <div className="cling-home__tour-copy-card">
                    <p className="cling-home__mini-eyebrow">Guided walkthrough</p>
                    <h3>See the merchant and customer story in one smooth narrative.</h3>
                    <p>
                      This is the fastest way for a new merchant, investor, partner, or customer to
                      understand how Cling turns loyalty into a broader local-commerce experience.
                    </p>
                    <ul className="cling-home__pillar-list">
                      {tourPoints.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="cling-home__hero-actions cling-home__hero-actions--compact">
                    <Link className="cling-home__button cling-home__button--primary" to="/VideoPlayer">
                      Watch product tour
                    </Link>
                    <a
                      className="cling-home__button cling-home__button--secondary"
                      href="https://play.google.com/store/apps/details?id=com.cling1.app"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download Cling
                    </a>
                  </div>
                </div>

                <Link className="cling-home__tour-visual" to="/VideoPlayer" aria-label="Open Cling product tour">
                  <div className="cling-home__tour-badge">Real app walkthrough</div>
                  <img
                    src={require("../assets/explainer/business-list.png")}
                    alt="Cling product tour preview"
                  />
                  <div className="cling-home__tour-play">
                    <span>Play</span>
                    <strong>Merchant setup, offers, loyalty, and customer wallet</strong>
                  </div>
                </Link>
              </div>
            </div>
          </section>

          <section className="cling-home__section cling-home__section--band" id="loyalty-wallet">
            <div className="cling-home__container cling-home__business-layout">
              <div className="cling-home__business-copy">
                <p className="cling-home__eyebrow">Loyalty first</p>
                <h2>A rewards program for merchants. A digital wallet for customers.</h2>
                <p>
                  This is one of Cling’s clearest product advantages. Merchants can enable loyalty
                  with less setup, and customers can carry multiple local reward relationships in
                  one app instead of scattered cards, punch systems, or separate merchant apps.
                </p>
                <ul className="cling-home__pillar-list">
                  {loyaltyPoints.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>

              <div className="cling-home__business-panel cling-home__business-panel--wallet">
                <img
                  className="cling-home__business-shot"
                  src={require("../assets/explainer/customer-programs.png")}
                  alt="Cling customer loyalty wallet"
                />
                <div className="cling-home__business-note">
                  <strong>One app for many merchants</strong>
                  <span>
                    Customers can discover nearby businesses and keep their rewards programs
                    together in one Cling wallet.
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="cling-home__section">
            <div className="cling-home__container cling-home__business-layout">
              <div className="cling-home__business-copy">
                <p className="cling-home__eyebrow">QR and reviews</p>
                <h2>Reviews can start on the web. The full standee experience lives in the app.</h2>
                <p>
                  That matters because a merchant QR should still lead somewhere useful. Cling can
                  support ratings and reviews on the web, while the richer standee journey for
                  loyalty, offers, and deeper merchant interaction is designed to work best inside
                  the app.
                </p>
                <ul className="cling-home__pillar-list">
                  {noAppPoints.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>

              <div className="cling-home__business-panel">
                <img
                  className="cling-home__business-shot cling-home__business-shot--tight"
                  src={require("../assets/explainer/business-standee.png")}
                  alt="Cling QR standee and review path"
                />
                <div className="cling-home__business-note">
                  <strong>Scan on the web. Go deeper in the app.</strong>
                  <span>
                    The standee can still capture ratings and reviews on the web, then encourage
                    customers to move into the app for the full Cling experience.
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="cling-home__section cling-home__section--tinted" id="app-preview">
            <div className="cling-home__container">
              <div className="cling-home__section-heading cling-home__section-heading--split">
                <div>
                  <p className="cling-home__eyebrow">Product preview</p>
                  <h2>Screens that connect local discovery, offers, reviews, and rewards.</h2>
                </div>
                <p>
                  These previews show the current product direction. We can swap in newer emulator
                  captures anytime to make the site even closer to the real app.
                </p>
              </div>

              <div className="cling-home__screens">
                {screenshotCards.map((screen) => (
                  <figure className="cling-home__screen-card" key={screen.label}>
                    <div className="cling-home__screen-label">{screen.label}</div>
                    <img src={screen.image} alt={screen.alt} />
                  </figure>
                ))}
              </div>
            </div>
          </section>

          <section className="cling-home__section">
            <div className="cling-home__container">
              <div className="cling-home__section-heading">
                <p className="cling-home__eyebrow">Core capabilities</p>
                <h2>Proof that loyalty is only the beginning of the merchant value story.</h2>
              </div>

              <div className="cling-home__feature-grid">
                {featureCards.map((feature) => (
                  <article className="cling-home__feature-card" key={feature.title}>
                    <h3>{feature.title}</h3>
                    <p>{feature.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="cling-home__section cling-home__section--band" id="businesses">
            <div className="cling-home__container cling-home__business-layout">
              <div className="cling-home__business-copy">
                <p className="cling-home__eyebrow">For businesses</p>
                <h2>Grow your business with Cling.</h2>
                <p>
                  Get discovered. Attract customers. Keep them coming back. Cling gives merchants a
                  simple entry point through loyalty, then expands their value through products,
                  offers, reviews, QR, and customer engagement.
                </p>
                <ul className="cling-home__pillar-list">
                  {businessPillars.map((pillar) => (
                    <li key={pillar}>{pillar}</li>
                  ))}
                </ul>
              </div>

              <div className="cling-home__business-panel">
                <img
                  className="cling-home__business-shot cling-home__business-shot--compact"
                  src={require("../assets/explainer/business-list.png")}
                  alt="Cling merchant all businesses screen"
                />
                <div className="cling-home__business-note">
                  <strong>Business profile spotlight</strong>
                  <span>
                    A merchant can launch rewards, showcase products, promote deals, and build
                    repeat customer behavior from one connected experience.
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="cling-home__section cling-home__section--contact" id="contact">
            <div className="cling-home__container">
              <div className="cling-home__contact-card">
                <div className="cling-home__contact-copy">
                  <p className="cling-home__eyebrow">Contact us</p>
                  <h2>Talk to Cling about merchants, partnerships, or rollout plans.</h2>
                  <p>
                    If you want to discuss merchant onboarding, local activation, partnerships, or
                    platform questions, you can reach the team directly. Cling™ is a product of
                    ClubIn Platforms Pvt. Ltd., an India-based company.
                  </p>
                </div>

                <div className="cling-home__contact-options">
                  {contactOptions.map((option) => (
                    <a className="cling-home__contact-option" key={option.label} href={option.href}>
                      <span>{option.label}</span>
                      <strong>{option.value}</strong>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="cling-home__section cling-home__section--tinted" id="ai-assistant">
            <div className="cling-home__container">
              <div className="cling-home__section-heading cling-home__section-heading--split">
                <div>
                  <p className="cling-home__eyebrow">Coming next</p>
                  <h2>An AI product assistant that helps local commerce feel smarter.</h2>
                </div>
                <p>
                  Cling is not trying to sound AI-first for the sake of it. The goal is practical
                  assistance that helps merchants sell better and helps customers discover and choose
                  products faster.
                </p>
              </div>

              <div className="cling-home__highlights">
                {aiPoints.map((point) => (
                  <article className="cling-home__highlight-card" key={point}>
                    <p className="cling-home__mini-eyebrow">AI value play</p>
                    <h3>Practical assistance, not hype</h3>
                    <p>{point}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="cling-home__section cling-home__section--trust">
            <div className="cling-home__container">
              <div className="cling-home__section-heading cling-home__section-heading--split">
                <div>
                  <p className="cling-home__eyebrow">Trust and compliance</p>
                  <h2>Keep important user-facing flows visible, not hidden.</h2>
                </div>
                <p>
                  We’re keeping the practical pieces of the site that matter for distribution,
                  support, and policy compliance while upgrading the overall experience.
                </p>
              </div>

              <div className="cling-home__trust-grid">
                {trustLinks.map((item) => (
                  <article className="cling-home__trust-card" key={item.title}>
                    <h3>{item.title}</h3>
                    <p>{item.copy}</p>
                    <Link to={item.to}>{item.cta}</Link>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="cling-home__section" id="leadership">
            <div className="cling-home__container">
              <div className="cling-home__section-heading cling-home__section-heading--split">
                <div>
                  <p className="cling-home__eyebrow">Leadership</p>
                  <h2>The team building Cling for long-term local growth.</h2>
                </div>
                <p>
                  Cling is being shaped by leaders across product, engineering, finance, and field
                  execution with a shared focus on building a meaningful local-commerce platform.
                </p>
              </div>

              <div className="cling-home__leadership-grid">
                {leadershipTeam.map((member) => (
                  <article className="cling-home__leader-card" key={member.name}>
                    <div className="cling-home__leader-photo">
                      <img src={member.image} alt={member.name} />
                    </div>
                    <div className="cling-home__leader-copy">
                      <p className="cling-home__leader-role">{member.role}</p>
                      <h3>{member.name}</h3>
                      <p>{member.bio}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </main>

        <footer className="cling-home__footer">
          <div className="cling-home__container cling-home__footer-inner">
            <div className="cling-home__footer-brand">
              <img src={require("../images/log-bw.svg").default} alt="Cling" />
              <p>
                Cling is building a more connected way to discover nearby businesses, offers, and
                communities.
              </p>
              <p className="cling-home__footer-legal">
                Cling™ is a product of ClubIn Platforms Pvt. Ltd., an India-based company.
              </p>
            </div>

            <div className="cling-home__footer-links">
              <div>
                <span>Explore</span>
                <button type="button" onClick={() => scrollToSection("product-story")}>
                  Product story
                </button>
                <button type="button" onClick={() => scrollToSection("loyalty-wallet")}>
                  Loyalty
                </button>
                <button type="button" onClick={() => scrollToSection("app-preview")}>
                  App screens
                </button>
                <button type="button" onClick={() => scrollToSection("businesses")}>
                  For businesses
                </button>
                <button type="button" onClick={() => scrollToSection("contact")}>
                  Contact
                </button>
                <button type="button" onClick={() => scrollToSection("ai-assistant")}>
                  AI assistant
                </button>
                <button type="button" onClick={() => scrollToSection("leadership")}>
                  Leadership
                </button>
              </div>
              <div>
                <span>Trust</span>
                <Link to="/PrivacyPolicy">Privacy notice</Link>
                <Link to="/UserAccountDelete">Delete account</Link>
                <Link to="/Feedback">Feedback</Link>
              </div>
              <div>
                <span>More</span>
                <Link to="/PostsPage">Posts preview</Link>
                <Link to="/VideoPlayer">Video</Link>
                <a
                  href="https://play.google.com/store/apps/details?id=com.cling1.app"
                  target="_blank"
                  rel="noreferrer"
                >
                  Google Play
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HomePage;
